import { useState } from "react";

/* ── Activity definitions ── */
type ActivityKey = "acta" | "repertory" | "kaplan" | "scenario";

interface Activity {
  key: ActivityKey;
  label: string;
  shortLabel: string;
  desc: string;
  icon: string;
  color: string;
}

const ACTIVITIES: Activity[] = [
  { key: "acta", label: "Cognitive Task Analysis", shortLabel: "ACTA", desc: "Walk through a case to surface hidden decision-making", icon: "A", color: "#6366f1" },
  { key: "repertory", label: "Repertory Grid", shortLabel: "Grid", desc: "Compare case pairs to discover implicit variables", icon: "R", color: "#0891b2" },
  { key: "kaplan", label: "Evidence Discussion", shortLabel: "Kaplan", desc: "Expert discusses, AI builds the distribution", icon: "K", color: "#059669" },
  { key: "scenario", label: "Scenario Prediction", shortLabel: "Scenario", desc: "Expert predicts outcomes for synthetic cases", icon: "S", color: "#f59e0b" },
];

/* ── ACTA Panel ── */
function ACTAPanel({ onInsert }: { onInsert: (prompt: string) => void }) {
  const [step, setStep] = useState(0);
  const steps = [
    { label: "Task Diagram", prompt: "Walk me through a typical custody evaluation from start to finish. What are the key decision points where your expertise matters most?", desc: "Map the overall decision process" },
    { label: "Knowledge Audit", prompt: "At the decision points you identified — what cues or patterns do you look for that a less experienced evaluator might miss? What makes you notice something is off?", desc: "Identify where expertise is critical" },
    { label: "Simulation", prompt: "Let me give you a specific case: Two parents, joint petition, one has stable income but limited involvement, the other is very involved but recently lost their job. Walk me through your assessment.", desc: "Expert reasons through a specific case" },
    { label: "Synthesis", prompt: "Looking back at your reasoning — what were the top 3 variables you weighed most heavily? For each, can you estimate how much it shifted your assessment (e.g., 'this accounted for about 30% of my decision')?", desc: "Extract implicit weights" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(99,102,241,0.6)", marginBottom: 2 }}>
        Step {step + 1} of {steps.length}: {steps[step].label}
      </div>

      {/* Step progress */}
      <div style={{ display: "flex", gap: 3 }}>
        {steps.map((s, i) => (
          <div key={i} onClick={() => setStep(i)} style={{
            flex: 1, height: 4, borderRadius: 2, cursor: "pointer",
            background: i <= step ? "#6366f1" : "rgba(0,0,0,0.08)",
            transition: "background 0.2s ease",
          }} />
        ))}
      </div>

      <div style={{ fontSize: 10, color: "rgba(0,0,0,0.45)", marginBottom: 4 }}>{steps[step].desc}</div>

      {/* Suggested prompt */}
      <div style={{
        padding: "10px 12px", borderRadius: 8, background: "rgba(99,102,241,0.06)",
        border: "1px solid rgba(99,102,241,0.12)", fontSize: 11.5, lineHeight: 1.55,
        color: "rgba(0,0,0,0.7)", fontStyle: "italic",
      }}>
        "{steps[step].prompt}"
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={() => onInsert(steps[step].prompt)} style={{
          flex: 1, padding: "8px 0", borderRadius: 6, border: "none", cursor: "pointer",
          background: "#6366f1", color: "#fff", fontSize: 10, fontWeight: 700,
        }}>
          Send to Chat
        </button>
        {step < steps.length - 1 && (
          <button onClick={() => setStep(step + 1)} style={{
            padding: "8px 12px", borderRadius: 6, border: "none", cursor: "pointer",
            background: "rgba(0,0,0,0.06)", color: "rgba(0,0,0,0.5)", fontSize: 10, fontWeight: 600,
          }}>
            Skip
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Repertory Grid Panel ── */
function RepertoryGridPanel({ onInsert }: { onInsert: (prompt: string) => void }) {
  const [pairIdx, setPairIdx] = useState(0);

  const casePairs = [
    {
      a: { label: "Case A", desc: "High income, low involvement, stable housing, history of anger management" },
      b: { label: "Case B", desc: "Low income, high involvement, unstable housing, no behavioral issues" },
    },
    {
      a: { label: "Case C", desc: "Both parents employed, moderate conflict, child age 4, no DV" },
      b: { label: "Case D", desc: "One parent stay-at-home, high conflict, child age 14, verbal DV allegations" },
    },
    {
      a: { label: "Case E", desc: "Parent with substance abuse history (3yr sober), strong family support" },
      b: { label: "Case F", desc: "Parent with no substance issues, but isolated with limited support network" },
    },
  ];

  const pair = casePairs[pairIdx];
  const prompt = `I'm going to show you two custody cases. Tell me: how are they fundamentally different in terms of likely outcome?\n\n${pair.a.label}: ${pair.a.desc}\n${pair.b.label}: ${pair.b.desc}\n\nWhat's the key dimension that separates these two cases?`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(8,145,178,0.7)" }}>
        Pair {pairIdx + 1} of {casePairs.length}
      </div>

      {/* Case cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {[pair.a, pair.b].map((c, i) => (
          <div key={i} style={{
            padding: "10px 12px", borderRadius: 8,
            background: i === 0 ? "rgba(8,145,178,0.06)" : "rgba(245,158,11,0.06)",
            border: `1px solid ${i === 0 ? "rgba(8,145,178,0.15)" : "rgba(245,158,11,0.15)"}`,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: i === 0 ? "#0891b2" : "#f59e0b", marginBottom: 4 }}>{c.label}</div>
            <div style={{ fontSize: 11, lineHeight: 1.5, color: "rgba(0,0,0,0.6)" }}>{c.desc}</div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 10, color: "rgba(0,0,0,0.4)", fontStyle: "italic" }}>
        "What's the key dimension that separates these cases?"
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={() => onInsert(prompt)} style={{
          flex: 1, padding: "8px 0", borderRadius: 6, border: "none", cursor: "pointer",
          background: "#0891b2", color: "#fff", fontSize: 10, fontWeight: 700,
        }}>
          Ask Expert
        </button>
        <button onClick={() => setPairIdx((pairIdx + 1) % casePairs.length)} style={{
          padding: "8px 12px", borderRadius: 6, border: "none", cursor: "pointer",
          background: "rgba(0,0,0,0.06)", color: "rgba(0,0,0,0.5)", fontSize: 10, fontWeight: 600,
        }}>
          Next Pair
        </button>
      </div>
    </div>
  );
}

/* ── Kaplan Panel ── */
function KaplanPanel({ onInsert }: { onInsert: (prompt: string) => void }) {
  const [phase, setPhase] = useState<"discuss" | "building">("discuss");

  const prompts = [
    "Tell me everything you know about how [this variable] affects custody outcomes. Don't worry about numbers — just describe what you've seen, what the research says, and what your instinct tells you.",
    "What evidence would change your mind about the importance of this variable? What would make you think it matters more or less than you currently believe?",
    "If you had to bet your reputation on a range — not a precise number, just a ballpark — where would you put the effect size of this variable?",
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(5,150,105,0.7)" }}>
        {phase === "discuss" ? "Evidence Discussion" : "AI Building Distribution"}
      </div>

      <div style={{
        padding: "10px 12px", borderRadius: 8, background: "rgba(5,150,105,0.06)",
        border: "1px solid rgba(5,150,105,0.12)", fontSize: 11, lineHeight: 1.55,
        color: "rgba(0,0,0,0.6)",
      }}>
        {phase === "discuss"
          ? "The expert discusses evidence freely. No numbers required. The AI listens and builds the probability distribution from their reasoning."
          : "Based on the discussion, the AI is constructing a distribution. The expert validates but doesn't need to specify parameters directly."
        }
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {prompts.map((p, i) => (
          <button key={i} onClick={() => onInsert(p)} style={{
            padding: "8px 12px", borderRadius: 6, border: "none", cursor: "pointer",
            background: "rgba(0,0,0,0.03)", color: "rgba(0,0,0,0.6)", fontSize: 10.5,
            textAlign: "left", lineHeight: 1.4, fontFamily: "inherit",
            transition: "background 0.15s ease",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(5,150,105,0.08)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(0,0,0,0.03)"; }}
          >
            {p.slice(0, 80)}...
          </button>
        ))}
      </div>

      <button onClick={() => setPhase(phase === "discuss" ? "building" : "discuss")} style={{
        padding: "8px 0", borderRadius: 6, border: "none", cursor: "pointer",
        background: phase === "discuss" ? "#059669" : "rgba(0,0,0,0.06)",
        color: phase === "discuss" ? "#fff" : "rgba(0,0,0,0.5)",
        fontSize: 10, fontWeight: 700,
      }}>
        {phase === "discuss" ? "Build Distribution from Discussion" : "Back to Discussion"}
      </button>
    </div>
  );
}

/* ── Scenario Panel ── */
function ScenarioPanel({ onInsert }: { onInsert: (prompt: string) => void }) {
  const [scenarioIdx, setScenarioIdx] = useState(0);

  const scenarios = [
    {
      label: "Scenario 1",
      factors: [
        { name: "Parental involvement", value: "High (15+ hrs/week)" },
        { name: "Income", value: "$45,000/yr" },
        { name: "DV history", value: "None" },
        { name: "Child age", value: "6 years" },
        { name: "Substance history", value: "None" },
      ],
    },
    {
      label: "Scenario 2",
      factors: [
        { name: "Parental involvement", value: "Low (3 hrs/week)" },
        { name: "Income", value: "$120,000/yr" },
        { name: "DV history", value: "Verbal, no physical" },
        { name: "Child age", value: "14 years" },
        { name: "Substance history", value: "Alcohol, 1yr sober" },
      ],
    },
    {
      label: "Scenario 3",
      factors: [
        { name: "Parental involvement", value: "Moderate (8 hrs/week)" },
        { name: "Income", value: "$72,000/yr" },
        { name: "DV history", value: "Mutual restraining orders" },
        { name: "Child age", value: "3 years" },
        { name: "Substance history", value: "Cannabis, active" },
      ],
    },
  ];

  const s = scenarios[scenarioIdx];
  const factorList = s.factors.map(f => `- ${f.name}: ${f.value}`).join("\n");
  const prompt = `Here's a custody case. On a scale of 1-10, how likely is a favorable custody outcome for this parent? Explain your reasoning and which factors weighed most heavily.\n\n${factorList}`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(245,158,11,0.8)" }}>
        {s.label} of {scenarios.length}
      </div>

      {/* Factor cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {s.factors.map(f => (
          <div key={f.name} style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "6px 10px", borderRadius: 6, background: "rgba(0,0,0,0.02)",
            fontSize: 11,
          }}>
            <span style={{ color: "rgba(0,0,0,0.5)" }}>{f.name}</span>
            <span style={{ fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace" }}>{f.value}</span>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 10, color: "rgba(0,0,0,0.4)", fontStyle: "italic" }}>
        "On a scale of 1-10, what's the likely outcome?"
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={() => onInsert(prompt)} style={{
          flex: 1, padding: "8px 0", borderRadius: 6, border: "none", cursor: "pointer",
          background: "#f59e0b", color: "#fff", fontSize: 10, fontWeight: 700,
        }}>
          Ask Expert to Predict
        </button>
        <button onClick={() => setScenarioIdx((scenarioIdx + 1) % scenarios.length)} style={{
          padding: "8px 12px", borderRadius: 6, border: "none", cursor: "pointer",
          background: "rgba(0,0,0,0.06)", color: "rgba(0,0,0,0.5)", fontSize: 10, fontWeight: 600,
        }}>
          Next
        </button>
      </div>
    </div>
  );
}

/* ── Main Activities Component ── */
export default function InterviewActivities({ onInsertPrompt }: { onInsertPrompt: (prompt: string) => void }) {
  const [activeActivity, setActiveActivity] = useState<ActivityKey | null>(null);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {!activeActivity ? (
        <>
          <div style={{ fontSize: 10, color: "rgba(0,0,0,0.4)", marginBottom: 2 }}>
            Choose an elicitation technique to guide the interview
          </div>
          {ACTIVITIES.map(act => (
            <div
              key={act.key}
              onClick={() => setActiveActivity(act.key)}
              style={{
                padding: "10px 12px", borderRadius: 8, cursor: "pointer",
                border: "1px solid rgba(0,0,0,0.06)", background: "#fff",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = act.color + "40"; e.currentTarget.style.background = act.color + "06"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(0,0,0,0.06)"; e.currentTarget.style.background = "#fff"; }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <div style={{
                  width: 22, height: 22, borderRadius: 5, fontSize: 10, fontWeight: 800,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: act.color + "14", color: act.color,
                }}>{act.icon}</div>
                <span style={{ fontSize: 12, fontWeight: 700 }}>{act.label}</span>
              </div>
              <div style={{ fontSize: 10, color: "rgba(0,0,0,0.45)", lineHeight: 1.4 }}>{act.desc}</div>
            </div>
          ))}
        </>
      ) : (
        <>
          {/* Back button */}
          <button onClick={() => setActiveActivity(null)} style={{
            display: "flex", alignItems: "center", gap: 4, padding: 0, border: "none",
            background: "none", cursor: "pointer", fontSize: 10, color: "rgba(0,0,0,0.4)",
            fontFamily: "inherit",
          }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            All Activities
          </button>

          {activeActivity === "acta" && <ACTAPanel onInsert={onInsertPrompt} />}
          {activeActivity === "repertory" && <RepertoryGridPanel onInsert={onInsertPrompt} />}
          {activeActivity === "kaplan" && <KaplanPanel onInsert={onInsertPrompt} />}
          {activeActivity === "scenario" && <ScenarioPanel onInsert={onInsertPrompt} />}
        </>
      )}
    </div>
  );
}
